import React, { useState, useEffect } from 'react';
import { useMutation, useQueryClient, useQuery } from '@tanstack/react-query';
import { billService } from '../../services/billService';
import { vendorService } from '../../services/vendorService';
import { jobService } from '../../services/jobService';
import productService, { Product } from '../../services/productService';
import { purchaseOrderService } from '../../services/purchaseOrderService';
import { CreateBillData, Bill, BillAttachment, BillStatus, RecurrencePattern } from '../../types/bill';
import { formatCurrency } from '../../utils/formatters';
import { DeleteBillModal } from './DeleteBillModal';

interface BillFormProps {
  bill?: Bill;
  onSuccess: () => void;
  onCancel: () => void;
}

interface BillItemForm {
  itemId: string;
  description: string;
  quantity: number;
  rate: number;
  taxRate?: number;
}

// ─── shared style tokens ─────────────────────────────────────────────────────
const inputStyle: React.CSSProperties = {
  width: '100%',
  padding: '9px 12px',
  background: '#f4efe2',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  fontFamily: "'Inter', sans-serif",
  fontSize: 12,
  color: '#5b5a52',
  outline: 'none',
  boxSizing: 'border-box',
};

const labelStyle: React.CSSProperties = {
  display: 'block',
  fontFamily: "'Inter', sans-serif",
  fontSize: 9,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  color: '#6b6a5c',
  marginBottom: 6,
};

const sectionTitleStyle: React.CSSProperties = {
  fontFamily: "'Inter', sans-serif",
  fontSize: 20,
  fontWeight: 300,
  color: '#1c2033',
  margin: 0,
};

const primaryBtnStyle: React.CSSProperties = {
  background: 'linear-gradient(135deg, #d97706, #b45309)',
  color: '#1c2033',
  border: 'none',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  fontWeight: 500,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const ghostBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#6b6a5c',
  border: '1px solid rgba(92,78,51,0.25)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const dangerBtnStyle: React.CSSProperties = {
  background: 'transparent',
  color: '#c95a5a',
  border: '1px solid rgba(201,90,90,0.3)',
  borderRadius: 4,
  padding: '10px 24px',
  fontFamily: "'Inter', sans-serif",
  fontSize: 10,
  letterSpacing: '0.2em',
  textTransform: 'uppercase',
  cursor: 'pointer',
};

const focusIn = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = 'rgba(217,119,6,0.35)';
};
const focusOut = (e: React.FocusEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
  e.target.style.borderColor = 'rgba(92,78,51,0.25)';
};

export const BillForm: React.FC<BillFormProps> = ({ bill, onSuccess, onCancel }) => {
  const queryClient = useQueryClient();
  const isEditing = !!bill;

  const [formData, setFormData] = useState<CreateBillData>({
    billNumber: '',
    vendorId: '',
    jobId: '',
    purchaseOrderId: '',
    billDate: new Date().toISOString().split('T')[0],
    dueDate: '',
    notes: '',
    status: 'DRAFT',
    isRecurring: false,
    recurrencePattern: undefined,
    recurrenceEndDate: '',
    items: []
  });

  const [items, setItems] = useState<BillItemForm[]>([
    { itemId: '', description: '', quantity: 1, rate: 0, taxRate: 0 }
  ]);

  const [errors, setErrors] = useState<string[]>([]);
  const [selectedFiles, setSelectedFiles] = useState<FileList | null>(null);
  const [filePreview, setFilePreview] = useState<string[]>([]);
  const [existingAttachments, setExistingAttachments] = useState<BillAttachment[]>([]);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Load data for dropdowns
  const { data: vendorsData, isLoading: vendorsLoading, error: vendorsError } = useQuery({
    queryKey: ['vendors'],
    queryFn: () => vendorService.getVendors({ limit: 1000 })
  });

  const { data: jobsData, isLoading: jobsLoading, error: jobsError } = useQuery({
    queryKey: ['jobs'],
    queryFn: () => jobService.getJobs({ limit: 1000 })
  });

  // Filter out completed and cancelled jobs for bill creation
  const activeJobs = jobsData?.data?.filter(job =>
    job.status !== 'COMPLETED' && job.status !== 'CANCELLED'
  ) || [];

  const { data: productsData, isLoading: productsLoading, error: productsError } = useQuery({
    queryKey: ['products'],
    queryFn: () => productService.getProducts({ limit: 1000 })
  });

  // Load purchase orders for the selected vendor
  const { data: purchaseOrdersData, isLoading: purchaseOrdersLoading } = useQuery({
    queryKey: ['purchase-orders-for-bill', formData.vendorId],
    queryFn: () => purchaseOrderService.getPurchaseOrders({
      vendorId: formData.vendorId,
      status: 'RECEIVED',
      limit: 1000
    }),
    enabled: !!formData.vendorId
  });

  useEffect(() => {
    if (bill) {
      setFormData({
        billNumber: bill.billNumber,
        vendorId: bill.vendorId,
        jobId: bill.jobId || '',
        purchaseOrderId: bill.purchaseOrderId || '',
        billDate: bill.billDate.split('T')[0],
        dueDate: bill.dueDate.split('T')[0],
        notes: bill.notes || '',
        status: bill.status,
        isRecurring: bill.isRecurring || false,
        recurrencePattern: bill.recurrencePattern,
        recurrenceEndDate: bill.recurrenceEndDate ? bill.recurrenceEndDate.split('T')[0] : '',
        items: bill.items?.map(item => ({
          itemId: item.itemId,
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          taxRate: item.taxRate || 0
        })) || []
      });

      if (bill.items) {
        setItems(bill.items.map(item => ({
          itemId: item.itemId,
          description: item.description,
          quantity: item.quantity,
          rate: item.rate,
          taxRate: item.taxRate || 0
        })));
      }

      if (bill.attachments) {
        setExistingAttachments(bill.attachments);
      }
    } else {
      setFormData(prev => ({ ...prev, billNumber: 'Will be auto-generated' }));
    }
  }, [bill]);

  // Calculate totals (only for items with meaningful data)
  const validItemsForCalculation = items.filter(item =>
    item.itemId || item.description.trim().length > 0 || item.rate > 0
  );
  const totals = billService.calculateBillTotals(validItemsForCalculation);

  const createMutation = useMutation({
    mutationFn: (data: CreateBillData) => {
      console.log('Create mutation called with selectedFiles:', selectedFiles);
      console.log('selectedFiles length:', selectedFiles?.length || 0);
      if (selectedFiles && selectedFiles.length > 0) {
        console.log('Using createBillWithAttachments');
        return billService.createBillWithAttachments(data, selectedFiles);
      } else {
        console.log('Using regular createBill');
        return billService.createBill(data);
      }
    },
    onSuccess: (createdBill) => {
      setSelectedFiles(null);
      if (createdBill?.attachments) {
        setExistingAttachments(createdBill.attachments);
      }
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      onSuccess();
    },
    onError: (error: any) => {
      setErrors([error.message || 'Failed to create bill']);
    }
  });

  const updateMutation = useMutation({
    mutationFn: (data: CreateBillData) => {
      console.log('Update mutation called with selectedFiles:', selectedFiles);
      console.log('selectedFiles length:', selectedFiles?.length || 0);
      if (selectedFiles && selectedFiles.length > 0) {
        console.log('Using updateBillWithAttachments');
        return billService.updateBillWithAttachments(bill!.id, data, selectedFiles);
      } else {
        console.log('Using regular updateBill');
        return billService.updateBill(bill!.id, data);
      }
    },
    onSuccess: async (updatedBill) => {
      setSelectedFiles(null);
      if (updatedBill?.attachments) {
        setExistingAttachments(updatedBill.attachments);
      }
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      if (bill?.id) {
        queryClient.invalidateQueries({ queryKey: ['bill', bill.id] });
      }
      onSuccess();
    },
    onError: (error: any) => {
      setErrors([error.message || 'Failed to update bill']);
    }
  });

  const deleteMutation = useMutation({
    mutationFn: () => {
      if (!bill?.id) throw new Error('No bill ID provided');
      return billService.deleteBill(bill.id);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['bills'] });
      setShowDeleteModal(false);
      onSuccess();
    },
    onError: (error: any) => {
      setErrors([error.message || 'Failed to delete bill']);
      setShowDeleteModal(false);
    }
  });

  const handleSubmit = (e: React.FormEvent) => {
    console.log('🚀 handleSubmit called! Form submission started.');
    e.preventDefault();

    const validItems = items.filter(item =>
      item.itemId || item.description.trim().length > 0 || item.rate > 0
    );

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    const { billNumber, ...requestData } = formData;

    const billData = isEditing ? {
      ...formData,
      items: validItems
    } : {
      ...requestData,
      items: validItems
    };

    console.log('Raw items array:', items);
    console.log('Filtered validItems:', validItems);
    console.log('BillData being validated:', billData);

    const validationErrors = billService.validateBillData(billData, isEditing);
    console.log('Validation errors returned:', validationErrors);

    if (validationErrors.length > 0) {
      console.log('Validation failed, setting errors and returning early');
      setErrors(validationErrors);
      return;
    }

    console.log('Validation passed, proceeding to mutation. isEditing:', isEditing);
    console.log('About to call mutation with billData:', billData);

    if (isEditing) {
      console.log('Calling updateMutation.mutate');
      updateMutation.mutate(billData);
    } else {
      console.log('Calling createMutation.mutate');
      createMutation.mutate(billData);
    }
  };

  const addItem = () => {
    setItems([...items, { itemId: '', description: '', quantity: 1, rate: 0, taxRate: 0 }]);
  };

  const removeItem = (index: number) => {
    if (items.length > 1) {
      setItems(items.filter((_, i) => i !== index));
    }
  };

  const updateItem = (index: number, field: keyof BillItemForm, value: any) => {
    const updatedItems = items.map((item, i) =>
      i === index ? { ...item, [field]: value } : item
    );
    setItems(updatedItems);
  };

  const handleItemSelect = (index: number, itemId: string) => {
    if (!itemId) {
      const updatedItems = [...items];
      updatedItems[index] = { ...updatedItems[index], itemId: '', description: '', rate: 0 };
      setItems(updatedItems);
      return;
    }

    const selectedProduct = productsData?.data.find((product: Product) => product.id === itemId);

    if (selectedProduct) {
      const updatedItems = [...items];
      updatedItems[index] = {
        ...updatedItems[index],
        itemId: itemId,
        description: selectedProduct.name,
        rate: selectedProduct.unitPrice || 0
      };
      setItems(updatedItems);
    }
  };

  const handlePurchaseOrderSelect = async (poId: string) => {
    setFormData({ ...formData, purchaseOrderId: poId });

    if (!poId) {
      setItems([{ itemId: '', description: '', quantity: 1, rate: 0, taxRate: 0 }]);
      return;
    }

    try {
      const selectedPO = await purchaseOrderService.getPurchaseOrder(poId);

      if (selectedPO.items && selectedPO.items.length > 0) {
        const billItems = selectedPO.items.map(poItem => ({
          itemId: poItem.productId,
          description: poItem.description,
          quantity: poItem.quantity,
          rate: poItem.rate,
          taxRate: poItem.taxRate || 0
        }));
        setItems(billItems);
      }
    } catch (error) {
      console.error('Error loading PO details:', error);
      setErrors(['Failed to load Purchase Order details']);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    console.log('File input changed, files:', files);
    if (files) {
      console.log('Setting selected files:', Array.from(files).map(f => f.name));
      setSelectedFiles(files);
      const fileNames = Array.from(files).map(file => file.name);
      setFilePreview(fileNames);
    }
  };

  const removeFile = (index: number) => {
    if (selectedFiles) {
      const filesArray = Array.from(selectedFiles);
      filesArray.splice(index, 1);
      const dt = new DataTransfer();
      filesArray.forEach(file => dt.items.add(file));
      setSelectedFiles(dt.files);
      const newPreview = [...filePreview];
      newPreview.splice(index, 1);
      setFilePreview(newPreview);
    }
  };

  const formatFileSize = (bytes: number) => {
    if (bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
  };

  const handleDeleteExistingAttachment = async (attachmentId: string) => {
    if (!bill?.id) return;
    try {
      await billService.deleteAttachment(bill.id, attachmentId);
      setExistingAttachments(prev => prev.filter(att => att.id !== attachmentId));
    } catch (error) {
      console.error('Error deleting attachment:', error);
      setErrors(['Failed to delete attachment']);
    }
  };

  const handleDownloadAttachment = async (attachment: BillAttachment) => {
    if (!bill?.id) return;
    try {
      const blob = await billService.downloadAttachment(bill.id, attachment.id);
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = attachment.originalName;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    } catch (error) {
      console.error('Error downloading attachment:', error);
      setErrors(['Failed to download attachment']);
    }
  };

  const isLoading = createMutation.isPending || updateMutation.isPending || deleteMutation.isPending;

  return (
    <div style={{
      position: 'fixed', inset: 0,
      background: 'rgba(7,7,15,0.85)',
      overflowY: 'auto',
      zIndex: 50,
      display: 'flex',
      alignItems: 'flex-start',
      justifyContent: 'center',
      padding: '40px 16px 40px',
    }}>
      <div style={{
        width: '100%',
        maxWidth: 900,
        background: '#ffffff',
        border: '1px solid rgba(92,78,51,0.15)',
        borderRadius: 8,
        overflow: 'hidden',
      }}>
        {/* Header */}
        <div style={{
          padding: '28px 32px 24px',
          borderBottom: '1px solid rgba(92,78,51,0.15)',
          background: '#faf7ee',
        }}>
          <h3 style={sectionTitleStyle}>
            {isEditing ? 'Edit Bill' : 'Create New Bill'}
          </h3>
          <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', marginTop: 4 }}>
            {isEditing ? 'Update bill details and line items' : 'Record a new vendor bill'}
          </p>
        </div>

        <div style={{ padding: '28px 32px', display: 'flex', flexDirection: 'column', gap: 28 }}>

          {/* Errors */}
          {errors.length > 0 && (
            <div style={{
              background: 'rgba(201,90,90,0.08)',
              border: '1px solid rgba(201,90,90,0.2)',
              borderRadius: 4,
              padding: 14,
            }}>
              <ul style={{ margin: 0, paddingLeft: 16, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#c95a5a' }}>
                {errors.map((error, index) => <li key={index}>{error}</li>)}
              </ul>
            </div>
          )}

          {/* Debug info */}
          {process.env.NODE_ENV === 'development' && (
            <div style={{
              background: 'rgba(241,245,249,1)',
              border: '1px solid rgba(92,78,51,0.2)',
              borderRadius: 4,
              padding: 10,
              fontFamily: "'Inter', sans-serif",
              fontSize: 11,
              color: '#6b6a5c',
            }}>
              <div>Products: {productsLoading ? 'Loading...' : `${productsData?.data?.length || 0} products loaded`}</div>
              <div>Vendors: {vendorsLoading ? 'Loading...' : `${vendorsData?.data?.length || 0} vendors loaded`}</div>
              <div>Jobs: {jobsLoading ? 'Loading...' : `${activeJobs.length} active jobs (${jobsData?.data?.length || 0} total loaded)`}</div>
              {productsError && <div style={{ color: '#c95a5a' }}>Products Error: {productsError.message}</div>}
              {vendorsError && <div style={{ color: '#c95a5a' }}>Vendors Error: {vendorsError.message}</div>}
              {jobsError && <div style={{ color: '#c95a5a' }}>Jobs Error: {jobsError.message}</div>}
            </div>
          )}

          <form onSubmit={handleSubmit} style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>

            {/* ── Bill Details Grid ── */}
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '16px 24px' }}>

              {/* Bill Number */}
              <div>
                <label style={labelStyle}>
                  Bill Number {isEditing ? <span style={{ color: '#c95a5a' }}>*</span> : <span style={{ opacity: 0.6 }}>(Auto-generated)</span>}
                </label>
                <input
                  type="text"
                  value={formData.billNumber}
                  onChange={(e) => setFormData({ ...formData, billNumber: e.target.value })}
                  style={{
                    ...inputStyle,
                    ...(!isEditing ? { background: '#0a0a18', color: '#8a8778', cursor: 'not-allowed' } : {})
                  }}
                  required={isEditing}
                  readOnly={!isEditing}
                  placeholder={!isEditing ? 'Will be auto-generated on creation' : ''}
                  onFocus={isEditing ? focusIn : undefined}
                  onBlur={isEditing ? focusOut : undefined}
                />
              </div>

              {/* Vendor */}
              <div>
                <label style={labelStyle}>Vendor <span style={{ color: '#c95a5a' }}>*</span></label>
                <select
                  value={formData.vendorId}
                  onChange={(e) => setFormData({ ...formData, vendorId: e.target.value })}
                  style={{ ...inputStyle, appearance: 'none' }}
                  required
                  onFocus={focusIn}
                  onBlur={focusOut}
                >
                  <option value="">Select a vendor</option>
                  {vendorsData?.data.map((vendor) => (
                    <option key={vendor.id} value={vendor.id}>{vendor.name}</option>
                  ))}
                </select>
              </div>

              {/* Job */}
              <div>
                <label style={labelStyle}>Job <span style={{ color: '#8a8778' }}>(Optional)</span></label>
                <select
                  value={formData.jobId || ''}
                  onChange={(e) => setFormData({ ...formData, jobId: e.target.value || undefined })}
                  style={{ ...inputStyle, appearance: 'none' }}
                  onFocus={focusIn}
                  onBlur={focusOut}
                >
                  <option value="">No Job</option>
                  {activeJobs.map((job) => (
                    <option key={job.id} value={job.id}>{job.jobNumber} - {job.jobName}</option>
                  ))}
                </select>
              </div>

              {/* Purchase Order */}
              <div>
                <label style={labelStyle}>Purchase Order <span style={{ color: '#8a8778' }}>(Optional)</span></label>
                <select
                  value={formData.purchaseOrderId || ''}
                  onChange={(e) => handlePurchaseOrderSelect(e.target.value)}
                  style={{
                    ...inputStyle,
                    appearance: 'none',
                    ...(!formData.vendorId || purchaseOrdersLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {})
                  }}
                  disabled={!formData.vendorId || purchaseOrdersLoading}
                  onFocus={focusIn}
                  onBlur={focusOut}
                >
                  <option value="">
                    {!formData.vendorId ? 'Select vendor first' : purchaseOrdersLoading ? 'Loading POs...' : 'No Purchase Order'}
                  </option>
                  {purchaseOrdersData?.purchaseOrders?.map((po) => (
                    <option key={po.id} value={po.id}>
                      {po.poNumber} - {formatCurrency(po.total)} ({new Date(po.orderDate).toLocaleDateString()})
                    </option>
                  ))}
                </select>
                {formData.purchaseOrderId && (
                  <p style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#d97706', marginTop: 4 }}>
                    Items will be populated from the selected Purchase Order
                  </p>
                )}
              </div>

              {/* Bill Date */}
              <div>
                <label style={labelStyle}>Bill Date</label>
                <input
                  type="date"
                  value={formData.billDate}
                  onChange={(e) => setFormData({ ...formData, billDate: e.target.value })}
                  style={{ ...inputStyle, colorScheme: 'light' }}
                  onFocus={focusIn}
                  onBlur={focusOut}
                />
              </div>

              {/* Due Date */}
              <div>
                <label style={labelStyle}>Due Date <span style={{ color: '#c95a5a' }}>*</span></label>
                <input
                  type="date"
                  value={formData.dueDate}
                  onChange={(e) => setFormData({ ...formData, dueDate: e.target.value })}
                  style={{ ...inputStyle, colorScheme: 'light' }}
                  required
                  onFocus={focusIn}
                  onBlur={focusOut}
                />
              </div>

              {/* Status */}
              <div style={{ gridColumn: '1 / -1' }}>
                <label style={labelStyle}>Status</label>
                <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap' }}>
                  {(['DRAFT', 'PENDING', 'APPROVED', 'CANCELLED'] as BillStatus[]).map((s) => {
                    const active = formData.status === s;
                    const colorMap: Record<string, string> = {
                      DRAFT: '#6b6a5c', PENDING: '#d97706', APPROVED: '#2563eb', CANCELLED: '#c95a5a'
                    };
                    const col = colorMap[s] || '#6b6a5c';
                    return (
                      <button
                        key={s}
                        type="button"
                        onClick={() => setFormData({ ...formData, status: s })}
                        style={{
                          padding: '7px 18px',
                          borderRadius: 4,
                          border: `1px solid ${active ? col : 'rgba(92,78,51,0.25)'}`,
                          background: active ? `${col}18` : 'transparent',
                          color: active ? col : '#6b6a5c',
                          fontFamily: "'Inter', sans-serif",
                          fontSize: 9,
                          fontWeight: active ? 600 : 400,
                          letterSpacing: '0.18em',
                          textTransform: 'uppercase',
                          cursor: 'pointer',
                          transition: 'all 0.15s',
                        }}
                      >
                        {s}
                      </button>
                    );
                  })}
                </div>
              </div>
            </div>

            {/* Notes */}
            <div>
              <label style={labelStyle}>Notes</label>
              <textarea
                value={formData.notes}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={3}
                placeholder="Additional notes..."
                style={{ ...inputStyle, resize: 'vertical', lineHeight: 1.6 }}
                onFocus={focusIn}
                onBlur={focusOut}
              />
            </div>

            {/* ── Recurring Bill Section ── */}
            <div style={{
              background: formData.isRecurring ? 'rgba(217,119,6,0.04)' : '#faf7ee',
              border: `1px solid ${formData.isRecurring ? 'rgba(217,119,6,0.25)' : 'rgba(92,78,51,0.15)'}`,
              borderRadius: 6,
              padding: '16px 20px',
              transition: 'all 0.2s',
            }}>
              {/* Toggle */}
              <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: formData.isRecurring ? 16 : 0 }}>
                <button
                  type="button"
                  onClick={() => setFormData({ ...formData, isRecurring: !formData.isRecurring, recurrencePattern: undefined, recurrenceEndDate: '' })}
                  style={{
                    width: 40, height: 22, borderRadius: 11, border: 'none', cursor: 'pointer',
                    background: formData.isRecurring ? '#d97706' : 'rgba(92,78,51,0.35)',
                    position: 'relative', transition: 'background 0.2s', flexShrink: 0,
                  }}
                >
                  <span style={{
                    position: 'absolute', top: 3, left: formData.isRecurring ? 21 : 3,
                    width: 16, height: 16, borderRadius: '50%', background: '#ffffff',
                    transition: 'left 0.2s', display: 'block',
                  }} />
                </button>
                <div>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, fontWeight: 600, color: '#1c2033' }}>
                    Recurring Bill
                  </span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#6b6a5c', marginLeft: 8 }}>
                    Automatically generate a new bill on a schedule
                  </span>
                </div>
              </div>

              {/* Recurring options */}
              {formData.isRecurring && (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '12px 24px' }}>
                  <div>
                    <label style={labelStyle}>Recurrence Pattern <span style={{ color: '#c95a5a' }}>*</span></label>
                    <select
                      value={formData.recurrencePattern || ''}
                      onChange={(e) => setFormData({ ...formData, recurrencePattern: e.target.value as RecurrencePattern || undefined })}
                      style={{ ...inputStyle, appearance: 'none' }}
                      required={formData.isRecurring}
                      onFocus={focusIn}
                      onBlur={focusOut}
                    >
                      <option value="">Select frequency</option>
                      <option value="WEEKLY">Weekly</option>
                      <option value="MONTHLY">Monthly</option>
                      <option value="QUARTERLY">Quarterly (every 3 months)</option>
                      <option value="YEARLY">Yearly</option>
                    </select>
                  </div>
                  <div>
                    <label style={labelStyle}>End Date <span style={{ color: '#8a8778' }}>(Optional)</span></label>
                    <input
                      type="date"
                      value={formData.recurrenceEndDate || ''}
                      onChange={(e) => setFormData({ ...formData, recurrenceEndDate: e.target.value })}
                      style={{ ...inputStyle, colorScheme: 'light' }}
                      onFocus={focusIn}
                      onBlur={focusOut}
                    />
                  </div>
                  <div style={{ gridColumn: '1 / -1' }}>
                    <div style={{
                      background: 'rgba(217,119,6,0.07)', border: '1px solid rgba(217,119,6,0.18)',
                      borderRadius: 4, padding: '8px 12px',
                      fontFamily: "'Inter', sans-serif", fontSize: 11, color: '#92400e',
                    }}>
                      📅 A new <strong>DRAFT</strong> bill with the same items will be generated automatically when this bill's period ends.
                      {formData.recurrencePattern && (
                        <span> Next bill will be due on the same day of the {
                          { WEEKLY: 'next week', MONTHLY: 'next month', QUARTERLY: 'next quarter', YEARLY: 'next year' }[formData.recurrencePattern]
                        }.</span>
                      )}
                    </div>
                  </div>
                </div>
              )}
            </div>

            {/* ── Line Items Section ── */}
            <div>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 16 }}>
                <span style={{
                  fontFamily: "'Inter', sans-serif",
                  fontSize: 10,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: '#6b6a5c',
                }}>
                  Line Items <span style={{ color: '#c95a5a' }}>*</span>
                </span>
                <button
                  type="button"
                  onClick={addItem}
                  style={{
                    background: 'transparent',
                    border: '1px solid rgba(92,78,51,0.3)',
                    borderRadius: 4,
                    padding: '5px 14px',
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 9,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: '#d97706',
                    cursor: 'pointer',
                  }}
                  onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(217,119,6,0.45)')}
                  onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(92,78,51,0.3)')}
                >
                  + Add Item
                </button>
              </div>

              {/* Column Headers */}
              <div style={{
                display: 'grid',
                gridTemplateColumns: '2.5fr 2.5fr 1fr 1.2fr 0.8fr 32px',
                gap: 8,
                padding: '0 0 8px',
                borderBottom: '1px solid rgba(92,78,51,0.15)',
                marginBottom: 4,
              }}>
                {['Product', 'Description', 'Qty', 'Rate', 'Tax %', ''].map((h, i) => (
                  <div key={i} style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 8,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: '#8a8778',
                  }}>
                    {h}
                  </div>
                ))}
              </div>

              {/* Item Rows */}
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {items.map((item, index) => (
                  <div
                    key={index}
                    style={{
                      display: 'grid',
                      gridTemplateColumns: '2.5fr 2.5fr 1fr 1.2fr 0.8fr 32px',
                      gap: 8,
                      padding: '10px 10px',
                      background: '#f4efe2',
                      borderBottom: '1px solid rgba(92,78,51,0.15)',
                      borderRadius: 4,
                      alignItems: 'center',
                    }}
                  >
                    {/* Product select */}
                    <select
                      value={item.itemId}
                      onChange={(e) => handleItemSelect(index, e.target.value)}
                      style={{ ...inputStyle, appearance: 'none', padding: '6px 10px', fontSize: 11 }}
                      disabled={productsLoading}
                      onFocus={focusIn}
                      onBlur={focusOut}
                    >
                      <option value="">{productsLoading ? 'Loading...' : 'Select product'}</option>
                      {productsData?.data?.map((productOption: Product) => (
                        <option key={productOption.id} value={productOption.id}>
                          {productOption.name} ({productOption.sku})
                        </option>
                      )) || []}
                      {productsError && <option value="" disabled>Error loading products</option>}
                    </select>

                    {/* Description */}
                    <input
                      type="text"
                      value={item.description}
                      onChange={(e) => updateItem(index, 'description', e.target.value)}
                      placeholder="Description"
                      style={{ ...inputStyle, padding: '6px 10px', fontSize: 11 }}
                      onFocus={focusIn}
                      onBlur={focusOut}
                    />

                    {/* Qty */}
                    <input
                      type="number"
                      value={item.quantity}
                      onChange={(e) => updateItem(index, 'quantity', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      style={{ ...inputStyle, padding: '6px 10px', fontSize: 11 }}
                      onFocus={focusIn}
                      onBlur={focusOut}
                    />

                    {/* Rate */}
                    <input
                      type="number"
                      value={item.rate}
                      onChange={(e) => updateItem(index, 'rate', parseFloat(e.target.value) || 0)}
                      min="0"
                      step="0.01"
                      style={{ ...inputStyle, padding: '6px 10px', fontSize: 11 }}
                      onFocus={focusIn}
                      onBlur={focusOut}
                    />

                    {/* Tax % */}
                    <input
                      type="number"
                      value={item.taxRate || 0}
                      onChange={(e) => updateItem(index, 'taxRate', parseFloat(e.target.value) || 0)}
                      min="0"
                      max="100"
                      step="0.1"
                      style={{ ...inputStyle, padding: '6px 10px', fontSize: 11 }}
                      onFocus={focusIn}
                      onBlur={focusOut}
                    />

                    {/* Remove */}
                    <div style={{ display: 'flex', justifyContent: 'center' }}>
                      {items.length > 1 && (
                        <button
                          type="button"
                          onClick={() => removeItem(index)}
                          style={{
                            background: 'transparent',
                            border: 'none',
                            color: '#c95a5a',
                            cursor: 'pointer',
                            fontSize: 14,
                            lineHeight: 1,
                            padding: 4,
                            opacity: 0.7,
                          }}
                          onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                          onMouseLeave={e => (e.currentTarget.style.opacity = '0.7')}
                          title="Remove item"
                        >
                          ✕
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>

              {/* Totals */}
              <div style={{
                marginTop: 16,
                padding: '16px 20px',
                background: '#faf7ee',
                border: '1px solid rgba(92,78,51,0.15)',
                borderRadius: 4,
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                alignItems: 'flex-end',
              }}>
                {[
                  { label: 'Subtotal', value: totals.subtotal },
                  { label: 'Tax', value: totals.taxAmount },
                ].map(({ label, value }) => (
                  <div key={label} style={{ display: 'flex', gap: 40, fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c' }}>
                    <span>{label}</span>
                    <span style={{ color: '#5b5a52', minWidth: 80, textAlign: 'right' }}>{formatCurrency(value)}</span>
                  </div>
                ))}
                <div style={{
                  display: 'flex',
                  gap: 40,
                  borderTop: '1px solid rgba(92,78,51,0.22)',
                  paddingTop: 8,
                  marginTop: 4,
                }}>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, letterSpacing: '0.15em', textTransform: 'uppercase', color: '#d97706' }}>Total</span>
                  <span style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 22,
                    fontWeight: 300,
                    color: '#1c2033',
                    minWidth: 80,
                    textAlign: 'right',
                    lineHeight: 1,
                  }}>
                    {formatCurrency(totals.total)}
                  </span>
                </div>
              </div>
            </div>

            {/* ── Attachments Section ── */}
            <div>
              <label style={{ ...labelStyle, marginBottom: 12 }}>Attachments <span style={{ color: '#8a8778' }}>(Optional)</span></label>

              {/* Existing Attachments */}
              {isEditing && existingAttachments.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 8,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: '#8a8778',
                    marginBottom: 8,
                  }}>
                    Current Attachments
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {existingAttachments.map((attachment) => (
                      <div
                        key={attachment.id}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          background: '#f4efe2',
                          border: '1px solid rgba(92,78,51,0.2)',
                          borderRadius: 4,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <svg width="14" height="14" fill="none" stroke="#8a8778" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>{attachment.originalName}</span>
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: '#6b6a5c' }}>({formatFileSize(attachment.fileSize)})</span>
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: '#5a4a35' }}>
                            {new Date(attachment.uploadedAt).toLocaleDateString()}
                          </span>
                        </div>
                        <div style={{ display: 'flex', gap: 12 }}>
                          <button
                            type="button"
                            onClick={() => handleDownloadAttachment(attachment)}
                            title="Download file"
                            style={{ background: 'none', border: 'none', color: '#d97706', cursor: 'pointer', fontSize: 14 }}
                          >
                            ⬇
                          </button>
                          <button
                            type="button"
                            onClick={() => handleDeleteExistingAttachment(attachment.id)}
                            title="Delete file"
                            style={{ background: 'none', border: 'none', color: '#c95a5a', cursor: 'pointer', fontSize: 14, opacity: 0.7 }}
                            onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                            onMouseLeave={e => (e.currentTarget.style.opacity = '0.7')}
                          >
                            ✕
                          </button>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Upload Area */}
              <div style={{
                border: '2px dashed rgba(92,78,51,0.25)',
                borderRadius: 6,
                padding: 24,
                textAlign: 'center',
                cursor: 'pointer',
              }}
                onMouseEnter={e => (e.currentTarget.style.borderColor = 'rgba(217,119,6,0.25)')}
                onMouseLeave={e => (e.currentTarget.style.borderColor = 'rgba(92,78,51,0.25)')}
              >
                <input
                  type="file"
                  multiple
                  onChange={handleFileChange}
                  style={{ display: 'none' }}
                  id="file-upload"
                  accept="*/*"
                />
                <label htmlFor="file-upload" style={{ cursor: 'pointer', display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8 }}>
                  <svg width="28" height="28" fill="none" stroke="#c9c2ab" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                  </svg>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#6b6a5c', fontWeight: 500 }}>
                    {isEditing ? 'Add more files' : 'Click to upload files'}
                  </span>
                  <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: '#8a8778' }}>
                    Support all file formats (PDF, JPG, PNG, DOC, etc.) — Max 10 MB each
                  </span>
                </label>
              </div>

              {/* New File Preview */}
              {selectedFiles && selectedFiles.length > 0 && (
                <div style={{ marginTop: 12 }}>
                  <div style={{
                    fontFamily: "'Inter', sans-serif",
                    fontSize: 8,
                    letterSpacing: '0.15em',
                    textTransform: 'uppercase',
                    color: '#8a8778',
                    marginBottom: 8,
                  }}>
                    {isEditing ? 'Files to Add' : 'Selected Files'}
                  </div>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                    {Array.from(selectedFiles).map((file, index) => (
                      <div
                        key={index}
                        style={{
                          display: 'flex',
                          alignItems: 'center',
                          justifyContent: 'space-between',
                          padding: '8px 12px',
                          background: 'rgba(241,245,249,1)',
                          border: '1px solid rgba(92,78,51,0.22)',
                          borderRadius: 4,
                        }}
                      >
                        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                          <svg width="14" height="14" fill="none" stroke="#d97706" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
                          </svg>
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 12, color: '#5b5a52' }}>{file.name}</span>
                          <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 10, color: '#6b6a5c' }}>({formatFileSize(file.size)})</span>
                          {isEditing && (
                            <span style={{ fontFamily: "'Inter', sans-serif", fontSize: 8, letterSpacing: '0.1em', color: '#d97706' }}>NEW</span>
                          )}
                        </div>
                        <button
                          type="button"
                          onClick={() => removeFile(index)}
                          style={{ background: 'none', border: 'none', color: '#c95a5a', cursor: 'pointer', fontSize: 14, opacity: 0.7 }}
                          onMouseEnter={e => (e.currentTarget.style.opacity = '1')}
                          onMouseLeave={e => (e.currentTarget.style.opacity = '0.7')}
                        >
                          ✕
                        </button>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>

            {/* ── Action Buttons ── */}
            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              paddingTop: 16,
              borderTop: '1px solid rgba(92,78,51,0.15)',
            }}>
              <div>
                {isEditing && (
                  <button
                    type="button"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={isLoading}
                    style={{ ...dangerBtnStyle, ...(isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                  >
                    Delete Bill
                  </button>
                )}
              </div>

              <div style={{ display: 'flex', gap: 12 }}>
                <button
                  type="button"
                  onClick={onCancel}
                  disabled={isLoading}
                  style={{ ...ghostBtnStyle, ...(isLoading ? { opacity: 0.5, cursor: 'not-allowed' } : {}) }}
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={isLoading}
                  onClick={() => console.log('🔥 Submit button clicked! isLoading:', isLoading)}
                  style={{ ...primaryBtnStyle, ...(isLoading ? { opacity: 0.7, cursor: 'not-allowed' } : {}) }}
                >
                  {isLoading ? 'Saving...' : (isEditing ? 'Update Bill' : 'Create Bill')}
                </button>
              </div>
            </div>
          </form>
        </div>
      </div>

      {/* Delete Confirmation Modal */}
      {bill && (
        <DeleteBillModal
          bill={bill}
          isOpen={showDeleteModal}
          isDeleting={deleteMutation.isPending}
          onConfirm={() => deleteMutation.mutate()}
          onCancel={() => setShowDeleteModal(false)}
        />
      )}
    </div>
  );
};
